import React, { useCallback, useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut, X, ChevronLeft, ChevronRight, RotateCcw, Maximize2 } from "lucide-react";

/**
 * Luxury product image gallery — premium watch / jewelry store experience.
 *
 * Features:
 *   • Thumbnail strip (vertical on desktop, horizontal on mobile)
 *   • Main image with hover magnifier (desktop) and click-to-zoom
 *   • Fullscreen lightbox: prev/next, ESC, keyboard arrows, zoom in/out, reset
 *   • Mobile: swipe between images, pinch-zoom enabled inside lightbox
 *
 * Props:
 *   images: string[]   — array of image URLs
 *   alt: string        — accessible name
 */
export default function ProductGallery({ images = [], alt = "" }) {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [magnify, setMagnify] = useState(null); // {x,y} in 0..1 over the main image
  const mainRef = useRef(null);
  const lightboxImgRef = useRef(null);
  const touchStartX = useRef(null);

  const safeImages = images.length > 0 ? images : ["https://images.unsplash.com/photo-1605100804763-247f67b2548e?auto=format&fit=crop&w=1200&q=80"];

  const next = useCallback(() => setActive((i) => (i + 1) % safeImages.length), [safeImages.length]);
  const prev = useCallback(() => setActive((i) => (i - 1 + safeImages.length) % safeImages.length), [safeImages.length]);
  const resetZoom = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  // Keyboard nav inside lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      else if (e.key === "ArrowRight") { next(); resetZoom(); }
      else if (e.key === "ArrowLeft") { prev(); resetZoom(); }
      else if (e.key === "+" || e.key === "=") setZoom((z) => Math.min(4, z + 0.5));
      else if (e.key === "-") setZoom((z) => Math.max(1, z - 0.5));
      else if (e.key === "0") resetZoom();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [lightbox, next, prev]);

  // Desktop hover magnifier
  const onHoverMove = (e) => {
    if (!mainRef.current) return;
    const rect = mainRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    setMagnify({ x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) });
  };
  const onHoverLeave = () => setMagnify(null);

  // Mobile swipe between images
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
    touchStartX.current = null;
  };

  // Lightbox pan when zoomed
  const onLightboxMouseDown = (e) => {
    if (zoom <= 1) return;
    const startX = e.clientX, startY = e.clientY;
    const startPan = { ...pan };
    const onMove = (m) => setPan({ x: startPan.x + (m.clientX - startX), y: startPan.y + (m.clientY - startY) });
    const onUp = () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const current = safeImages[active];

  return (
    <>
      <div data-testid="product-gallery" className="flex gap-4">
        {/* Thumbnails — vertical on desktop */}
        <div className="hidden md:flex flex-col gap-3 w-20 shrink-0">
          {safeImages.map((img, i) => (
            <button key={i} data-testid={`thumb-${i}`} onClick={() => setActive(i)}
              className={`aspect-square overflow-hidden transition-all ${i === active ? "ring-2 ring-[#C8A96A] opacity-100" : "ring-1 ring-border opacity-60 hover:opacity-100"}`}>
              <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>

        {/* Main image */}
        <div className="flex-1">
          <div
            ref={mainRef}
            data-testid="main-image"
            onMouseMove={onHoverMove}
            onMouseLeave={onHoverLeave}
            onClick={() => { setLightbox(true); resetZoom(); }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className="relative aspect-[4/5] overflow-hidden bg-[#F5F2EC] cursor-zoom-in group"
          >
            <img src={current} alt={alt} className="w-full h-full object-cover select-none" draggable={false} />
            {/* Hover magnifier (desktop only) */}
            {magnify && (
              <div
                className="hidden md:block absolute pointer-events-none border-2 border-[#C8A96A] shadow-2xl overflow-hidden bg-white"
                style={{
                  width: 220, height: 220,
                  left: `calc(${magnify.x * 100}% - 110px)`,
                  top: `calc(${magnify.y * 100}% - 110px)`,
                  backgroundImage: `url(${current})`,
                  backgroundSize: "300% 300%",
                  backgroundPosition: `${magnify.x * 100}% ${magnify.y * 100}%`,
                  backgroundRepeat: "no-repeat",
                }}
              />
            )}
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[10px] uppercase tracking-wider px-2 py-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Maximize2 size={10} /> Tam Ekran
            </div>
            {safeImages.length > 1 && (
              <div className="absolute bottom-3 left-3 bg-black/60 text-white text-[10px] uppercase tracking-wider px-2 py-1">
                {active + 1} / {safeImages.length}
              </div>
            )}
          </div>

          {/* Horizontal thumbnails — mobile only */}
          <div className="md:hidden flex gap-2 mt-3 overflow-x-auto">
            {safeImages.map((img, i) => (
              <button key={i} onClick={() => setActive(i)}
                className={`shrink-0 w-16 h-16 ${i === active ? "ring-2 ring-[#C8A96A]" : "ring-1 ring-border"}`}>
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div data-testid="gallery-lightbox" className="fixed inset-0 bg-black/95 z-[100] flex flex-col" role="dialog" aria-modal="true">
          {/* Top toolbar */}
          <div className="flex items-center justify-between p-4 text-white">
            <div className="text-xs uppercase tracking-[0.2em]">{active + 1} / {safeImages.length}</div>
            <div className="flex items-center gap-1">
              <IconBtn onClick={() => setZoom((z) => Math.max(1, z - 0.5))} title="Uzaklaştır" testid="lb-zoom-out"><ZoomOut size={18} /></IconBtn>
              <span className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</span>
              <IconBtn onClick={() => setZoom((z) => Math.min(4, z + 0.5))} title="Yakınlaştır" testid="lb-zoom-in"><ZoomIn size={18} /></IconBtn>
              <IconBtn onClick={resetZoom} title="Sıfırla" testid="lb-zoom-reset"><RotateCcw size={16} /></IconBtn>
              <IconBtn onClick={() => setLightbox(false)} title="Kapat (ESC)" testid="lb-close"><X size={20} /></IconBtn>
            </div>
          </div>
          {/* Image area */}
          <div
            className="relative flex-1 overflow-hidden flex items-center justify-center"
            onClick={(e) => { if (e.target === e.currentTarget) setLightbox(false); }}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <img
              ref={lightboxImgRef}
              src={current}
              alt={alt}
              data-testid="lightbox-image"
              onMouseDown={onLightboxMouseDown}
              className="max-w-full max-h-full object-contain transition-transform duration-200 select-none"
              draggable={false}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                cursor: zoom > 1 ? "grab" : "zoom-in",
              }}
              onClick={(e) => { e.stopPropagation(); zoom === 1 ? setZoom(2) : resetZoom(); }}
            />
            {safeImages.length > 1 && (
              <>
                <button data-testid="lb-prev" onClick={() => { prev(); resetZoom(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors">
                  <ChevronLeft size={28} />
                </button>
                <button data-testid="lb-next" onClick={() => { next(); resetZoom(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors">
                  <ChevronRight size={28} />
                </button>
              </>
            )}
          </div>
          {/* Bottom thumbnail strip */}
          {safeImages.length > 1 && (
            <div className="px-4 py-3 overflow-x-auto">
              <div className="flex gap-2 justify-center">
                {safeImages.map((img, i) => (
                  <button key={i} onClick={() => { setActive(i); resetZoom(); }}
                    className={`shrink-0 w-16 h-16 transition-all ${i === active ? "ring-2 ring-[#C8A96A] opacity-100" : "ring-1 ring-white/20 opacity-50 hover:opacity-100"}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

function IconBtn({ onClick, title, testid, children }) {
  return (
    <button onClick={onClick} title={title} data-testid={testid}
      className="p-2 hover:bg-white/10 transition-colors text-white">
      {children}
    </button>
  );
}
